import { useMemo } from "react";
import { getCellFormatType } from "./adapter";
import type { AiScriptSpreadsheetViewerProps } from "./ai-script-spreadsheet-viewer-types";
import { isOptimisticManualCell } from "./optimistic-manual-row-edits";
import { RowInsertMenu } from "./row-insert-menu";
import {
  getSavedRowInsertAnchor,
  getSavedSpreadsheetRow,
} from "./spreadsheet-region-view";
import type { SpreadsheetViewer } from "./use-spreadsheet-viewer";
import { emptySelection, getEditedCellSelection } from "./utils";

type ViewerRowMenuProps = Pick<
  AiScriptSpreadsheetViewerProps,
  "onAskAiAboutCell" | "onViewEditedCell" | "onNoteFromSelection"
> & {
  viewer: SpreadsheetViewer;
};

export const ViewerRowMenu = ({
  viewer,
  onAskAiAboutCell,
  onViewEditedCell,
  onNoteFromSelection,
}: ViewerRowMenuProps) => {
  const {
    sheetLayout,
    ui: {
      rowMenu,
      setRowMenu,
      gridSelection,
      setGridSelection,
      setIsMaximized,
      setRegionDialogTarget,
    },
    sheet: {
      adaptedSheet,
      activeRegions,
      activeSheetValue,
      canMutateActiveSheet,
      hasActiveRegionView,
      regionRowMap,
      frozenRow,
    },
    comments: {
      areCommentsEnabled,
      commentIndex,
      savedCellCoordinate,
      openCommentCard,
    },
    pointer: { getCellQuestionContext, getCellLink, getCellSource },
    cellEdits: { formatCell },
    rowActions: { insertManualRow, deleteManualRowById },
  } = viewer;

  const rowMenuQuestion = rowMenu
    ? getCellQuestionContext(rowMenu.col, rowMenu.gridRow)
    : null;

  const rowMenuCellRecord =
    rowMenu && adaptedSheet
      ? adaptedSheet.getCellRecord(rowMenu.col, rowMenu.gridRow)
      : undefined;

  const rowMenuEditedCell = getEditedCellSelection(rowMenuCellRecord);
  const getNoteGridText = rowMenu?.getNoteGridText;
  const rowMenuGridRow = rowMenu?.gridRow;
  const canFreezeThroughRow = useMemo(() => {
    if (rowMenuGridRow === undefined) return false;
    for (let row = 0; row <= rowMenuGridRow; row += 1) {
      if (getSavedSpreadsheetRow(regionRowMap, row) !== row) return false;
    }
    return true;
  }, [regionRowMap, rowMenuGridRow]);

  const handleViewEdit = () => {
    if (!rowMenu || !rowMenuEditedCell || !onViewEditedCell) return;

    setGridSelection({
      ...emptySelection,
      current: {
        cell: [rowMenu.col, rowMenu.gridRow],
        range: { x: rowMenu.col, y: rowMenu.gridRow, width: 1, height: 1 },
        rangeStack: [],
      },
    });
    onViewEditedCell(rowMenuEditedCell);
    setRowMenu(null);
  };

  const rowMenuRegion =
    rowMenu && rowMenuCellRecord
      ? activeRegions.find(
          (region) =>
            rowMenuCellRecord.row >= region.header_row &&
            rowMenuCellRecord.row <= region.record_end_row &&
            rowMenuCellRecord.col >= region.start_col &&
            rowMenuCellRecord.col <= region.end_col,
        )
      : undefined;

  const rowMenuManualCell =
    rowMenu && adaptedSheet
      ? adaptedSheet.getManualRowCell(rowMenu.gridRow)
      : undefined;

  const commentCoordinate =
    areCommentsEnabled && rowMenu
      ? savedCellCoordinate(rowMenu.col, rowMenu.gridRow)
      : null;
  const commentActionLabel = commentCoordinate
    ? commentIndex.hasOpenThreadAt(commentCoordinate.row, commentCoordinate.col)
      ? "Reply to comment"
      : commentIndex.threadsAt(commentCoordinate.row, commentCoordinate.col)
            .length > 0
        ? "View comments"
        : "Comment"
    : null;

  return (
    <RowInsertMenu
      target={rowMenu}
      cellLink={rowMenu ? getCellLink(rowMenu.col, rowMenu.gridRow) : null}
      onShowSource={
        rowMenu ? getCellSource(rowMenu.col, rowMenu.gridRow) : null
      }
      onViewEdit={
        rowMenuEditedCell && onViewEditedCell ? handleViewEdit : undefined
      }
      onNoteForNextPeriod={
        onNoteFromSelection && getNoteGridText
          ? () => {
              const text = getNoteGridText();
              setRowMenu(null);
              onNoteFromSelection(text);
            }
          : undefined
      }
      isLayoutSaving={sheetLayout.isSaving}
      isFreezeRowsDisabled={!canFreezeThroughRow}
      onFreezeRows={
        sheetLayout.canEdit && rowMenu
          ? () => {
              if (!canFreezeThroughRow) return;
              const savedRow = getSavedSpreadsheetRow(
                regionRowMap,
                rowMenu.gridRow,
              );
              sheetLayout.onFrozenRowChange(savedRow + 1);
              setRowMenu(null);
            }
          : undefined
      }
      onUnfreezeRows={
        sheetLayout.canEdit && frozenRow !== null
          ? () => {
              sheetLayout.onFrozenRowChange(null);
              setRowMenu(null);
            }
          : undefined
      }
      onAskAi={
        rowMenuQuestion
          ? () => {
              setGridSelection(rowMenuQuestion.selection);
              onAskAiAboutCell?.(rowMenuQuestion.context);
              setRowMenu(null);
              setIsMaximized(false);
            }
          : undefined
      }
      canInsertRow={canMutateActiveSheet}
      commentActionLabel={commentActionLabel}
      onCommentAction={() => {
        if (rowMenu) openCommentCard(rowMenu.col, rowMenu.gridRow, true);
        setRowMenu(null);
      }}
      canFormatCell={
        !!rowMenuCellRecord &&
        canMutateActiveSheet &&
        !rowMenuCellRecord.locked &&
        !isOptimisticManualCell(rowMenuCellRecord)
      }
      currentValueType={
        rowMenuCellRecord ? getCellFormatType(rowMenuCellRecord) : null
      }
      canDeleteRow={
        !!rowMenuManualCell?.manual_row_id &&
        canMutateActiveSheet &&
        !isOptimisticManualCell(rowMenuManualCell)
      }
      tableActionLabel={
        canMutateActiveSheet
          ? rowMenuRegion
            ? "Edit table…"
            : "Create table from selection…"
          : null
      }
      isTableActionDisabled={hasActiveRegionView}
      onTableAction={() => {
        setRegionDialogTarget({
          sheetId: activeSheetValue,
          selection: gridSelection,
          existingRegion: rowMenuRegion,
        });
        setRowMenu(null);
      }}
      onClose={() => setRowMenu(null)}
      onInsertRow={(displayRow, position) =>
        insertManualRow(
          getSavedRowInsertAnchor(regionRowMap, displayRow, position),
        )
      }
      onFormatCell={(valueType) => {
        if (rowMenuCellRecord) formatCell(rowMenuCellRecord, valueType);
      }}
      onDeleteRow={() => deleteManualRowById(rowMenuManualCell?.manual_row_id)}
    />
  );
};
